import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { Formik } from 'formik'
import { endOfDay } from 'date-fns'

import Api from 'services/Api'
import BaseModal from 'components/Modals/BaseModal'
import { closeModal } from 'store/actions/modals'
import { Button, DateTimePicker, Checkbox, Select, Form } from 'components/Forms'
import { Loading } from 'components/UI'
import { Format, Parse } from 'services/Dates'
import { addEvents } from 'store/actions/scheduleTemplates'
import { parseToTemplate, somewhatRandomID } from 'services/ScheduleTemplates'
import { Alert, Card } from 'ui'

import { t } from 'translations'

const ScheduleTemplatesImportScheduleModal = ({ scheduleTemplateId, numberOfDaysInTemplate, closeModal, addEvents }) => {
  const [template, setTemplate] = useState()
  const [errorLoading, setErrorLoading] = useState(false)

  useEffect(() => {
    const fetchScheduleTemplate = async () => {
      const { result, success } = await Api(`schedule/template`, { params: { _id: scheduleTemplateId, useNewReturn: true } })
      if (!success || !result.length) return setErrorLoading(true)
      return setTemplate(result[0])
    }

    fetchScheduleTemplate()
  }, [scheduleTemplateId])

  const importFromSchedule = async ({ date, keepAssignedEmployees, importOnlyFromThisTemplate, multiDayIndex }, actions) => {
    try {
      const rangeStart = Parse(date)
      const params = { rangeStart: Format(rangeStart), rangeEnd: Format(endOfDay(rangeStart)), useNewReturn: true }

      // Add required positions to query if relevant.
      if (Array.isArray(template.requiredPositions) && template.requiredPositions.length) params.positions = template.requiredPositions

      const { result, success } = await Api('/schedule/schedule', { params })

      if (!success) throw new Error('Error importing from schedule')
      addEvents(
        result
          .map(event => {
            // Option to remove assigned employees
            if (!keepAssignedEmployees) delete event.employeeId
            event.id = somewhatRandomID()

            return { ...parseToTemplate(event), multiDayIndex }
          })
          // Option to keep only shifts from this template
          .filter(({ templateId }) => (importOnlyFromThisTemplate ? ['new', templateId].includes(scheduleTemplateId) : true))
      )
      closeModal()
    } catch (error) {
      actions.setStatus({ type: 'danger', msg: 'manager.scheduleTemplates.IMPORT_SCHEDULE_MODAL.ERROR_IMPORTING' })
      actions.setSubmitting(false)
    }
  }

  if (errorLoading) return <BaseModal title={`manager.scheduleTemplates.IMPORT_SCHEDULE_MODAL`}>{t('manager.scheduleTemplates.TEMPLATE_DELETED')}</BaseModal>
  if (!template) return <Loading />

  return (
    <BaseModal hideButtons title={`manager.scheduleTemplates.IMPORT_SCHEDULE_MODAL`}>
      <Formik
        enableReinitialize={true}
        initialValues={{ date: '', multiDayIndex: 0, keepAssignedEmployees: true, importOnlyFromThisTemplate: false }}
        onSubmit={importFromSchedule}
      >
        {({ isSubmitting, status, setStatus }) => {
          return (
            <Form className="mt-2">
              {/* Show warning when importing and template has required positions that only required positions will be imported. */}
              <Alert variant="warning" className="mt-2" show={Array.isArray(template.requiredPositions) && template.requiredPositions.length > 0}>
                {t('manager.scheduleTemplates.IMPORT_SCHEDULE_MODAL.DESCRIPTION')}
              </Alert>

              {/* Error message */}
              <Alert variant={status?.type} onClose={() => setStatus()} show={!!status?.msg}>
                {status?.msg}
              </Alert>
              <Card className="bg-light mt-2">
                <Card.Body>
                  <DateTimePicker module="manager.scheduleTemplates.IMPORT_SCHEDULE_MODAL" type="date" name="date" required />
                  <Checkbox module="manager.scheduleTemplates.IMPORT_SCHEDULE_MODAL" name="keepAssignedEmployees" />
                  <Checkbox module="manager.scheduleTemplates.IMPORT_SCHEDULE_MODAL" name="importOnlyFromThisTemplate" />
                  {/* This option is only shown in multi-day templates */}
                  {numberOfDaysInTemplate > 1 ? (
                    <Select
                      module="manager.scheduleTemplates.form"
                      name="multiDayIndex"
                      options={new Array(numberOfDaysInTemplate)
                        .fill(1)
                        .map((option, index) => ({ _id: index, name: t('manager.scheduleTemplates.form.MULTIDAYINDEX.OPTION', { day: index + 1 }) }))}
                    />
                  ) : null}
                </Card.Body>
              </Card>
              <div className="mt-2">
                <Button className="btn btn-primary mr-2" type="submit" isSubmitting={isSubmitting} text="SUBMIT" />
                <Button text="actions.CLOSE" className="btn btn-outline-secondary" onClick={() => closeModal()} />
              </div>
            </Form>
          )
        }}
      </Formik>
    </BaseModal>
  )
}

const mapStateToProps = ({ scheduleTemplates }) => ({ numberOfDaysInTemplate: scheduleTemplates.days })
const mapDispatchToProps = dispatch => ({ closeModal: () => dispatch(closeModal()), addEvents: (...args) => dispatch(addEvents(...args)) })

export default connect(mapStateToProps, mapDispatchToProps)(ScheduleTemplatesImportScheduleModal)
